package com.wintermar.inspection.feature.data.remote.mapper.inspection

import com.wintermar.inspection.core.data.Uuid
import com.wintermar.inspection.feature.domain.model.VesselModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerFileModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerLocationModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerModel
import com.wintermar.inspection.feature.domain.model.answer.AnswerSelectionModel
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.model.template.InspectionPageModel
import com.wintermar.inspection.feature.domain.model.template.InspectionSectionModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateModel
import com.wintermar.inspection.feature.domain.model.template.InspectionTemplateQuestionModel
import com.wintermar.inspection.feature.domain.model.template.TemplateOptionModel
import com.wintermar.inspection.graphql.InspectionByIdWithMediaUrlQuery
import com.wintermar.inspection.graphql.InspectionListQuery
import com.wintermar.inspection.graphql.type.InspectionAnswerType
import com.wintermar.inspection.graphql.type.InspectionStatus
import migrations.InspectionTable

fun List<InspectionTable>.toListInspectionModel(): List<InspectionModel> {
    return this.map { it.toInspectionModel() }
}

fun InspectionTable.toInspectionModel(): InspectionModel {
    return InspectionModel(
        id = this.id,
        templateId = this.templateId,
        templateName = this.templateName,
        vesselName = this.vesselName ?: "",
        status = InspectionStatus.safeValueOf(
            this.status.ifEmpty {
                InspectionStatus.UNKNOWN__.rawValue
            }),
        lastUpdated = this.lastUpdated,
        totalAnswer = this.totalAnswer.toInt(),
        totalQuestion = this.totalQuestion.toInt(),
        conducted = this.conducted ?: ""
    )
}

fun List<InspectionListQuery.Data1>.toInspectionListFromApi(
    vesselList: List<VesselModel>
): List<InspectionModel> {
    return this.map {
        val vesselName = vesselList.find { vessel -> vessel.id == it.vesselName }?.name ?: ""
        InspectionModel(
            id = it.id.toString(),
            templateId = it.templateId.toString(),
            templateName = it.inspectionName,
            vesselName = vesselName,
            lastUpdated = it.lastUpdatedAt?.toString() ?: "",
            status = it.status,
            conducted = it.conductedAt.toString(),
            totalAnswer = it.answeredQuestions,
            totalQuestion = it.totalQuestions
        )
    }
}

fun InspectionByIdWithMediaUrlQuery.InspectionByIdWithMediaUrl.toInspectionModelFromApi(
    templateOptions: List<TemplateOptionModel>,
    userFullName: String
): InspectionModel {
    val vesselName = this.template.pages
        .flatMap { it.sections }
        .flatMap { it.questions }
        .find { it.answerType == InspectionAnswerType.VESSEL_ID }
        ?.answers?.displayAnswer ?: ""

    return InspectionModel(
        id = this.id.toString(),
        templateId = this.templateId.toString(),
        templateName = this.template.name,
        vesselName = vesselName,
        lastUpdated = this.lastUpdatedAt?.toString() ?: "",
        conducted = this.conductedAt.toString(),
        status = this.status,
        totalAnswer = this.template.pages.flatMap { it.sections }.sumOf { section ->
            section.questions.count { question -> question.answers != null }
        },
        totalQuestion = this.template.numberOfQuestions ?: 0,
        template = InspectionTemplateModel(
            id = this.template.id.toString(),
            name = this.template.name,
            version = this.template.version,
            publishedDate = this.template.publishedDate.toString(),
            numberOfPages = this.template.numberOfPages ?: 0,
            numberOfSections = this.template.numberOfSections ?: 0,
            numberOfQuestions = this.template.numberOfQuestions ?: 0,
            pages = this.template.pages.map { page ->
                InspectionPageModel(
                    id = page.id.toString(),
                    templateId = page.templateId.toString(),
                    order = page.order,
                    title = page.title,
                    sections = page.sections.map { section ->
                        InspectionSectionModel(
                            id = section.id.toString(),
                            pageId = section.pageId.toString(),
                            order = section.order,
                            title = section.title,
                            questions = section.questions.map { question ->
                                InspectionTemplateQuestionModel(
                                    id = question.id.toString(),
                                    sectionId = question.sectionId.toString(),
                                    questionText = question.questionText,
                                    guidelines = question.guidelines ?: "",
                                    required = question.required,
                                    order = question.order,
                                    answerType = question.answerType,
                                    failedOptionId = question.failedOptionId.toString(),
                                    neutralOptionId = question.neutralOptionId.toString(),
                                    answerOptions = templateOptions,
                                    answer = if (question.answers == null) AnswerModel() else
                                        AnswerModel(
                                            id = question.answers.id.toString(),
                                            inspectionId = this.id.toString(),
                                            questionId = question.id.toString(),
                                            notes = question.answers.notes ?: "",
                                            answerDateIso = if (question.answerType == InspectionAnswerType.CALENDAR)
                                                question.answers.answer.toString() else "",
                                            answerOptionId =
                                                if (question.answerType == InspectionAnswerType.YES_NO_NA
                                                    || question.answerType == InspectionAnswerType.COMPLIANCE_NON_COMPLIANCE_NA
                                                )
                                                    question.answers.optionId.toString() else "",
                                            answerText = if (question.answerType == InspectionAnswerType.FREE_TEXT) question.answers.answer.toString() else "",
                                            answerLocation = if (question.answerType == InspectionAnswerType.ADDRESS) AnswerLocationModel(
                                                id = Uuid.generate(),
                                                answerId = question.answers.id.toString(),
                                                name = question.answers.answer.toString()
                                            ) else AnswerLocationModel(),
                                            answerSelection = if (question.answerType == InspectionAnswerType.USER_ID
                                                || question.answerType == InspectionAnswerType.VESSEL_ID
                                            )
                                                AnswerSelectionModel(
                                                    id = Uuid.generate(),
                                                    answerId = question.answers.id.toString(),
                                                    answerSelectionId = question.answers.answer.toString(),
                                                    answerSelectionValue = if (question.answerType == InspectionAnswerType.USER_ID) userFullName else vesselName
                                                ) else AnswerSelectionModel(),
                                            files = question.answers.media?.map { file ->
                                                AnswerFileModel(
                                                    answerId = question.answers.id.toString(),
                                                    id = file.id.toString(),
                                                    name = file.fileName,
                                                    originalName = file.originalFileName.trim(),
                                                    size = file.size.toLong(),
                                                    apiFileUploaded = file.fileUploaded ?: false,
                                                    path = file.fileUrl ?: "",
                                                    thumbnailPath = file.thumbnailUrl ?: ""
                                                )
                                            } ?: emptyList(),
                                            isRequiredFileOrNotes = question.answers.optionId.toString() == question.failedOptionId,
                                            lastUpdatedIsoUtc0 = question.answers.lastUpdatedAt?.toString()
                                                ?: "",
                                            isSubmitted = true,
                                            findingCategory = question.answers.findingCategory
                                        )
                                )
                            }
                        )
                    }
                )
            }
        )
    )
}
